import {
  Add,
  ArrowBack,
  Delete,
  Edit,
  Refresh,
  Search,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  DataGridPremium,
  GridActionsCellItem,
  GridColDef,
} from '@mui/x-data-grid-premium';
import { EndpointUrl } from 'api/endpoint';
import { useField, useFieldTypes } from 'api/fieldEditor';
import { useCurrentUser } from 'api/providers/authProvider';
import { useWorkspaceId } from 'api/providers/workspaceProvider';
import { useNotifications } from 'components/notify';
import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatName } from 'utils/fields';

export default function FieldEditorFieldsPage() {
  const params = useParams();
  const navigate = useNavigate();
  const fieldTypes = useFieldTypes();
  const workspaceId = useWorkspaceId();
  const user = useCurrentUser();
  const { showNotification } = useNotifications();

  const fieldTypeParam = params.fieldType ?? '';

  const { data: selectedFieldType } = useMemo(() => {
    return {
      ...fieldTypes,
      data:
        (fieldTypes.data?.findIndex((x) => x.type === fieldTypeParam) ?? -1) >=
        0
          ? fieldTypeParam
          : null,
    };
  }, [fieldTypeParam, fieldTypes]);

  const fieldData = useField(selectedFieldType);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState<string | null>(null);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldType, setNewFieldType] = useState('string');
  const [searchQuery, setSearchQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fieldTypeOptions = [
    'string',
    'number',
    'boolean',
    'date',
    'image',
    'file',
    'entity',
    'object',
    'enum',
  ];

  const filteredFields = useMemo(() => {
    if (!fieldData.data) return [];
    if (!searchQuery.trim()) return fieldData.data;

    const query = searchQuery.toLowerCase();
    return fieldData.data.filter(
      (field) =>
        field.name.toLowerCase().includes(query) ||
        formatName(field.name).toLowerCase().includes(query) ||
        field.schema.type.toLowerCase().includes(query),
    );
  }, [fieldData.data, searchQuery]);

  const handleNewField = async () => {
    if (!newFieldName.trim() || !selectedFieldType) {
      showNotification('Field name is required', { variant: 'error' });
      return;
    }

    setCreating(true);
    try {
      const fieldPayload = {
        type: selectedFieldType,
        name: newFieldName.trim(),
        schema: { type: newFieldType },
      };

      const response = await fetch(
        `${EndpointUrl}/workspace/${workspaceId}/field/${fieldPayload.type}/${fieldPayload.name}`,
        {
          method: 'POST',
          body: JSON.stringify(fieldPayload),
          headers: {
            'Content-Type': 'application/json',
            'x-finsight-token': user?.token || '',
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to create field');
      }

      showNotification('Field created successfully', { variant: 'success' });
      setCreateDialogOpen(false);
      setNewFieldName('');
      setNewFieldType('string');
      fieldData.mutate();
    } catch (error) {
      console.error('Failed to create field:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create field';
      showNotification(errorMessage, { variant: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteField = async () => {
    if (!fieldToDelete || !selectedFieldType) return;

    setDeleting(true);
    try {
      const response = await fetch(
        `${EndpointUrl}/workspace/${workspaceId}/field/${selectedFieldType}/${fieldToDelete}`,
        {
          method: 'DELETE',
          headers: {
            'x-finsight-token': user?.token || '',
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete field');
      }

      showNotification('Field deleted successfully', { variant: 'success' });
      setDeleteDialogOpen(false);
      setFieldToDelete(null);
      fieldData.mutate();
    } catch (error) {
      console.error('Failed to delete field:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to delete field';
      showNotification(errorMessage, { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const handleRefresh = () => {
    fieldData.mutate();
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 150,
        renderCell: (params) => (
          <Typography variant="body2" fontWeight="medium">
            {formatName(params.value)}
          </Typography>
        ),
      },
      {
        field: 'type',
        headerName: 'Type',
        flex: 1,
        minWidth: 100,
        valueGetter: (_, row) => row.schema.type,
        renderCell: (params) => (
          <Chip label={params.value} size="small" variant="outlined" />
        ),
      },
      {
        field: 'sortable',
        headerName: 'Sortable',
        width: 100,
        valueGetter: (_, row) => row.schema.sortable ?? false,
        renderCell: (params) => (
          <Chip
            label={params.value ? 'Yes' : 'No'}
            size="small"
            color={params.value ? 'success' : 'default'}
            variant={params.value ? 'filled' : 'outlined'}
          />
        ),
      },
      {
        field: 'required',
        headerName: 'Required',
        width: 100,
        valueGetter: (_, row) => row.schema.required ?? false,
        renderCell: (params) => (
          <Chip
            label={params.value ? 'Yes' : 'No'}
            size="small"
            color={params.value ? 'error' : 'default'}
            variant={params.value ? 'filled' : 'outlined'}
          />
        ),
      },
      {
        field: 'order',
        headerName: 'Order',
        width: 100,
        valueGetter: (_, row) => row.schema.order ?? 0,
      },
      {
        field: 'multiple',
        headerName: 'Multiple',
        width: 100,
        valueGetter: (_, row) => row.schema.multiple ?? false,
        renderCell: (params) => (
          <Chip
            label={params.value ? 'Yes' : 'No'}
            size="small"
            color={params.value ? 'info' : 'default'}
            variant={params.value ? 'filled' : 'outlined'}
          />
        ),
      },
      {
        field: 'groupable',
        headerName: 'Groupable',
        width: 120,
        valueGetter: (_, row) => row.schema.groupable ?? false,
        renderCell: (params) => (
          <Chip
            label={params.value ? 'Yes' : 'No'}
            size="small"
            color={params.value ? 'primary' : 'default'}
            variant={params.value ? 'filled' : 'outlined'}
          />
        ),
      },
      {
        field: 'default',
        headerName: 'Default',
        flex: 1,
        minWidth: 120,
        valueGetter: (_, row) => {
          const defaultValue = row.schema.default;
          if (defaultValue === undefined || defaultValue === null) {
            return 'None';
          }
          return String(defaultValue);
        },
        renderCell: (params) => (
          <Typography variant="body2" color="text.secondary">
            {params.value}
          </Typography>
        ),
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: 150,
        getActions: (params) => [
          <GridActionsCellItem
            key="edit"
            icon={<Edit />}
            label="Edit"
            onClick={() => navigate(`${params.row.name}`)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<Delete />}
            label="Delete"
            onClick={() => {
              setFieldToDelete(params.row.name);
              setDeleteDialogOpen(true);
            }}
            showInMenu
          />,
        ],
      },
    ],
    [navigate],
  );

  if (!selectedFieldType) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          Type not found. Please select a valid type.
        </Alert>
      </Box>
    );
  }

  if (fieldData.error) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load fields. Please try again.</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate(`/workspace/${workspaceId}/field-editor`)}
          sx={{ cursor: 'pointer' }}
        >
          Types
        </Link>
        <Typography color="text.primary">
          {formatName(selectedFieldType)}
        </Typography>
      </Breadcrumbs>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Stack direction="row" alignItems="center" gap={2}>
          <IconButton
            onClick={() => navigate(`/workspace/${workspaceId}/field-editor`)}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" component="h1">
            {formatName(selectedFieldType)} Fields
          </Typography>
        </Stack>
        <Stack direction="row" gap={1}>
          <IconButton onClick={handleRefresh} disabled={!fieldData.data}>
            <Refresh />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
            disabled={!selectedFieldType}
          >
            New Field
          </Button>
        </Stack>
      </Stack>

      <TextField
        fullWidth
        placeholder="Search fields..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
      />

      {!fieldData.data ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      ) : filteredFields.length === 0 ? (
        <Alert severity="info">
          {searchQuery
            ? 'No fields found matching your search.'
            : 'No fields available. Create your first field to get started.'}
        </Alert>
      ) : (
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGridPremium
            rows={filteredFields}
            columns={columns}
            getRowId={(row) => row.name}
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
              sorting: { sortModel: [{ field: 'order', sort: 'desc' }] },
            }}
            disableRowSelectionOnClick
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </Box>
      )}

      {/* Create Field Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => !creating && setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Field</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <TextField
              autoFocus
              label="Field Name"
              fullWidth
              value={newFieldName}
              onChange={(e) => setNewFieldName(e.target.value)}
              placeholder="e.g., status, priority, description"
              helperText="Enter a unique field name (lowercase, underscores allowed)"
              disabled={creating}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && newFieldName.trim()) {
                  handleNewField();
                }
              }}
            />
            <TextField
              label="Field Type"
              fullWidth
              select
              SelectProps={{
                native: true,
              }}
              value={newFieldType}
              onChange={(e) => setNewFieldType(e.target.value)}
              disabled={creating}
            >
              {fieldTypeOptions.map((type) => (
                <option key={type} value={type}>
                  {formatName(type)}
                </option>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCreateDialogOpen(false)}
            disabled={creating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleNewField}
            variant="contained"
            disabled={!newFieldName.trim() || creating}
            startIcon={creating ? <CircularProgress size={16} /> : <Add />}
          >
            {creating ? 'Creating...' : 'Create Field'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Field Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Field</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the field &quot;
            {formatName(fieldToDelete || '')}&quot;? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteField}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <Delete />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
